import type { DiagramElement, Drill } from "./drill";

export type History = { past: Drill[]; present: Drill; future: Drill[] };
export type EditorAction =
  | { type: "replace"; drill: Drill }
  | { type: "update"; update: Partial<Drill> }
  | { type: "add-element"; element: DiagramElement }
  | { type: "add-elements"; elements: DiagramElement[] }
  | { type: "update-element"; id: string; update: Partial<DiagramElement> }
  | { type: "update-elements"; ids: string[]; update: Partial<DiagramElement> }
  | { type: "delete-element"; id: string }
  | { type: "delete-elements"; ids: string[] }
  | { type: "clear" }
  | { type: "undo" }
  | { type: "redo" };

const checkpoint = (state: History, next: Drill): History => ({
  past: [...state.past.slice(-49), state.present],
  present: { ...next, updatedAt: new Date().toISOString() },
  future: [],
});

export function historyReducer(state: History, action: EditorAction): History {
  switch (action.type) {
    case "replace":
      return { past: [], present: action.drill, future: [] };
    case "update":
      return checkpoint(state, { ...state.present, ...action.update });
    case "add-element":
      return checkpoint(state, {
        ...state.present,
        elements: [...state.present.elements, action.element],
      });
    case "add-elements":
      return checkpoint(state, {
        ...state.present,
        elements: [...state.present.elements, ...action.elements],
      });
    case "update-element":
      return checkpoint(state, {
        ...state.present,
        elements: state.present.elements.map((element) =>
          element.id === action.id
            ? ({ ...element, ...action.update } as DiagramElement)
            : element,
        ),
      });
    case "update-elements":
      return checkpoint(state, {
        ...state.present,
        elements: state.present.elements.map((element) =>
          action.ids.includes(element.id)
            ? ({ ...element, ...action.update } as DiagramElement)
            : element,
        ),
      });
    case "delete-element":
      return checkpoint(state, {
        ...state.present,
        elements: state.present.elements.filter(
          (element) => element.id !== action.id,
        ),
      });
    case "delete-elements":
      return checkpoint(state, {
        ...state.present,
        elements: state.present.elements.filter(
          (element) => !action.ids.includes(element.id),
        ),
      });
    case "clear":
      return checkpoint(state, { ...state.present, elements: [] });
    case "undo": {
      const previous = state.past.at(-1);
      if (!previous) return state;
      return {
        past: state.past.slice(0, -1),
        present: previous,
        future: [state.present, ...state.future],
      };
    }
    case "redo": {
      const next = state.future[0];
      if (!next) return state;
      return {
        past: [...state.past, state.present],
        present: next,
        future: state.future.slice(1),
      };
    }
  }
}
